import { useEffect, useMemo, useRef } from 'react'
import { useVirtualizer } from '@tanstack/react-virtual'
import { Play } from 'lucide-react'

import { shortLabel, SpeakerStyleMap, styleForAttribution } from './colors'
import { formatMs } from './Swimlane'

import styles from './TranscriptColumns.module.scss'

import Button from '@/components/Button'
import {
  type AttributionRow,
  filterRows,
  mergeConsecutiveSpeakerRows,
} from '@/pages/app/AttributionViewer/artifactDerivations'

// Per index i, the row in rows[0..i] with the latest end_ms (later rows win ties).
export const prefixMaxEnd = (rows: AttributionRow[]): number[] => {
  const result: number[] = []
  rows.forEach((row, index) => {
    const previous = result[index - 1]
    result.push(index > 0 && rows[previous].end_ms > row.end_ms ? previous : index)
  })
  return result
}

// Index of the row covering T, else the nearest by gap. -1 when the column is empty.
export const activeIndex = (
  rows: AttributionRow[],
  t: number | null,
  maxEnd: number[] = prefixMaxEnd(rows)
): number => {
  if (t == null || rows.length === 0) return -1
  let low = 0
  let high = rows.length
  while (low < high) {
    const mid = Math.floor((low + high) / 2)
    if (rows[mid].start_ms <= t) low = mid + 1
    else high = mid
  }
  const before = low - 1
  if (before < 0) return 0
  // Live mic/system rows overlap, so a row that started earlier can still cover T after a later one ended.
  for (let index = before; index >= 0 && t <= rows[maxEnd[index]].end_ms; index -= 1) {
    if (t <= rows[index].end_ms) return index
  }
  const nearestEnded = maxEnd[before]
  if (low >= rows.length) return nearestEnded
  return t - rows[nearestEnded].end_ms <= rows[low].start_ms - t ? nearestEnded : low
}

type ColumnProps = {
  id: string
  title: string
  rows: AttributionRow[]
  activeTime: number | null
  speakerStyles: SpeakerStyleMap
  showText?: boolean
  emptyMessage?: string
  onPlayFrom?: (startMs: number, endMs: number) => void
}

const ColumnHeader = ({ title }: { title: string }) => (
  <div className={styles.colHeader}>
    <span className={styles.colTitle}>{title}</span>
  </div>
)

const Column = ({ id, title, rows, activeTime, speakerStyles, showText, emptyMessage, onPlayFrom }: ColumnProps) => {
  const bodyRef = useRef<HTMLDivElement>(null)
  const displayRows = useMemo(() => (showText ? rows : mergeConsecutiveSpeakerRows(rows)), [rows, showText])
  const maxEnd = useMemo(() => prefixMaxEnd(displayRows), [displayRows])
  const activeRowIndex = useMemo(() => activeIndex(displayRows, activeTime, maxEnd), [displayRows, activeTime, maxEnd])
  const virtualizer = useVirtualizer({
    count: displayRows.length,
    getScrollElement: () => bodyRef.current,
    estimateSize: () => (showText ? 58 : 34),
    overscan: 8,
  })

  useEffect(() => {
    if (activeRowIndex >= 0) virtualizer.scrollToIndex(activeRowIndex, { align: 'center' })
  }, [activeRowIndex, virtualizer])

  return (
    <div className={styles.column} data-column={id}>
      <ColumnHeader title={title} />
      <div className={styles.colBody} ref={bodyRef}>
        {displayRows.length === 0 ? (
          <div className={styles.colEmpty}>{emptyMessage ?? '(no data for this meeting)'}</div>
        ) : (
          <div className={styles.virtualRows} style={{ height: virtualizer.getTotalSize() }}>
            {virtualizer.getVirtualItems().map((virtualRow) => {
              const row = displayRows[virtualRow.index]
              const speakerStyle = styleForAttribution(row, speakerStyles)
              return (
                <div
                  key={virtualRow.key}
                  ref={virtualizer.measureElement}
                  data-index={virtualRow.index}
                  className={`${styles.utter} ${virtualRow.index === activeRowIndex ? styles.utterActive : ''}`}
                  style={{ transform: `translateY(${virtualRow.start}px)` }}
                >
                  <span className={styles.uHead}>
                    {onPlayFrom && (
                      <Button
                        title={<Play size={12} strokeWidth={2.2} aria-hidden="true" />}
                        type="link"
                        className={styles.playBtn}
                        ariaLabel={`Play from ${formatMs(row.start_ms)} to ${formatMs(row.end_ms)}`}
                        onClick={() => onPlayFrom(row.start_ms, row.end_ms)}
                      />
                    )}
                    <span className={styles.uTime}>{formatMs(row.start_ms)}</span>
                    <span className={styles.uSpeaker}>
                      <span
                        className={styles.swatch}
                        data-pattern={speakerStyle.pattern}
                        style={{ backgroundColor: speakerStyle.color }}
                      />
                      {shortLabel(speakerStyle)}
                    </span>
                    {id !== 'live' && row.detail && <span className={styles.uMuted}>{row.detail}</span>}
                    {id === 'live' && row.providerDetail && <span className={styles.uMuted}>{row.providerDetail}</span>}
                    {row.method && <span className={styles.uMethod}>{row.method}</span>}
                  </span>
                  {showText && row.text && <span className={styles.uText}>{row.text}</span>}
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}

export type TranscriptColumn = {
  id: string
  title: string
  state: 'loading' | 'present' | 'absent' | 'error'
  rows: AttributionRow[]
  showText?: boolean
  emptyMessage?: string
  onRetry?: () => void
}

const columnStateMessage = (state: TranscriptColumn['state']): string => {
  if (state === 'loading') return 'Loading artifact…'
  if (state === 'absent') return 'No data for this meeting'
  if (state === 'error') return 'Artifact could not be read'
  return ''
}

type TranscriptColumnsProps = {
  columns: TranscriptColumn[]
  activeTime: number | null
  speakerStyles: SpeakerStyleMap
  zoomed: boolean
  viewStart: number
  viewEnd: number
  onPlayFrom?: (startMs: number, endMs: number) => void
}

const TranscriptColumns = ({
  columns,
  activeTime,
  speakerStyles,
  zoomed,
  viewStart,
  viewEnd,
  onPlayFrom,
}: TranscriptColumnsProps) => {
  const visibleColumns = useMemo(
    () =>
      columns.map((column) => ({
        ...column,
        rows: zoomed ? filterRows(column.rows, viewStart, viewEnd) : column.rows,
      })),
    [columns, zoomed, viewStart, viewEnd]
  )

  return (
    <section className={styles.columns} aria-label="Transcripts">
      {visibleColumns.map((column) =>
        column.state === 'present' ? (
          <Column
            key={column.id}
            id={column.id}
            title={column.title}
            rows={column.rows}
            activeTime={activeTime}
            speakerStyles={speakerStyles}
            showText={column.showText}
            emptyMessage={column.emptyMessage}
            onPlayFrom={onPlayFrom}
          />
        ) : (
          <div key={column.id} className={styles.column} data-column={column.id}>
            <ColumnHeader title={column.title} />
            <div className={styles.columnState}>
              <span>
                {column.state === 'absent' && column.emptyMessage
                  ? column.emptyMessage
                  : columnStateMessage(column.state)}
              </span>
              {column.state === 'error' && column.onRetry && (
                <Button title="Retry" type="light" onClick={column.onRetry} />
              )}
            </div>
          </div>
        )
      )}
    </section>
  )
}

export default TranscriptColumns
